import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";
import {
  ShieldCheck,
  UserPlus,
  KeyRound,
  Trash2,
  Check,
  X,
  Clock,
  BadgeCheck,
} from "lucide-react";

// Делегирование: реестр интеграторов, гранты на ботов клиентов, очередь needs-review.
// Бэкенд: server/admin/routes/delegation.ts. Авторизация — authorizeBotConfig.

const CAPABILITIES = ["config", "prompt", "knowledge", "tools", "channels", "voice", "crm"] as const;

interface Integrator {
  id: string;
  userId: string;
  kind: string;
  displayName: string | null;
  status: string;
  certifiedAt: string | null;
}
interface Grant {
  id: string;
  integratorId: string;
  scope: string;
  botId: string | null;
  targetUserId: string | null;
  capabilities: string[];
  autoApprove: boolean;
  status: string;
}
interface PendingChange {
  id: string;
  botId: string;
  integratorId: string;
  changeType: string;
  status: string;
  createdAt: string | null;
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold mb-4">
        {icon}
        {title}
      </h2>
      {children}
    </div>
  );
}

export default function DelegationPage() {
  const [selectedIntegrator, setSelectedIntegrator] = useState<string | null>(null);

  // ===== Integrators =====
  const integratorsQuery = useQuery<Integrator[]>({ queryKey: ["/api/admin/integrators"] });

  const [newUserId, setNewUserId] = useState("");
  const [newKind, setNewKind] = useState<"external" | "internal">("external");
  const [newName, setNewName] = useState("");

  const createIntegrator = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/integrators", {
        userId: newUserId.trim(),
        kind: newKind,
        displayName: newName.trim() || undefined,
      }),
    onSuccess: () => {
      setNewUserId("");
      setNewName("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/integrators"] });
    },
  });

  const certify = useMutation({
    mutationFn: ({ id, certified }: { id: string; certified: boolean }) =>
      apiRequest("POST", `/api/admin/integrators/${id}/certify`, { certified }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/integrators"] }),
  });

  // ===== Grants (per selected integrator) =====
  const grantsQuery = useQuery<Grant[]>({
    queryKey: ["/api/admin/integrators", selectedIntegrator, "grants"],
    enabled: !!selectedIntegrator,
  });

  const [grantScope, setGrantScope] = useState<"bot" | "account">("bot");
  const [grantBotId, setGrantBotId] = useState("");
  const [grantTargetUserId, setGrantTargetUserId] = useState("");
  const [grantCaps, setGrantCaps] = useState<string[]>(["config"]);
  const [grantAuto, setGrantAuto] = useState(false);

  const createGrant = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/grants", {
        integratorId: selectedIntegrator,
        scope: grantScope,
        botId: grantScope === "bot" ? grantBotId.trim() : undefined,
        targetUserId: grantScope === "account" ? grantTargetUserId.trim() : undefined,
        capabilities: grantCaps,
        autoApprove: grantAuto,
      }),
    onSuccess: () => {
      setGrantBotId("");
      setGrantTargetUserId("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/integrators", selectedIntegrator, "grants"] });
    },
  });

  const revokeGrant = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/grants/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/integrators", selectedIntegrator, "grants"] }),
  });

  // ===== Pending changes =====
  const pendingQuery = useQuery<PendingChange[]>({ queryKey: ["/api/admin/pending-changes"] });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approve" | "reject" }) =>
      apiRequest("POST", `/api/admin/pending-changes/${id}/${decision}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/pending-changes"] }),
  });

  function toggleCap(cap: string) {
    setGrantCaps((prev) => (prev.includes(cap) ? prev.filter((c) => c !== cap) : [...prev, cap]));
  }

  const btn = "px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50";
  const input = "px-2 py-1.5 rounded-md border border-border bg-background text-sm";

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-bold mb-1">Делегирование</h1>
      <p className="text-muted-foreground text-sm mb-6">
        Интеграторы (внешние/внутренние) настраивают ботов клиентов: scoped + audited + needs-review.
        Сертификация открывает autoApprove (правки live, без ревью).
      </p>

      {/* ============ ИНТЕГРАТОРЫ ============ */}
      <Section title="Интеграторы" icon={<ShieldCheck className="w-5 h-5 text-primary" />}>
        {/* create form */}
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <input className={input} placeholder="userId" value={newUserId} onChange={(e) => setNewUserId(e.target.value)} />
          <select className={input} value={newKind} onChange={(e) => setNewKind(e.target.value as "external" | "internal")}>
            <option value="external">внешний</option>
            <option value="internal">внутренний</option>
          </select>
          <input className={input} placeholder="имя (опц.)" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <button
            className={`${btn} bg-primary text-primary-foreground flex items-center gap-1`}
            disabled={!newUserId.trim() || createIntegrator.isPending}
            onClick={() => createIntegrator.mutate()}
          >
            <UserPlus className="w-4 h-4" /> Добавить
          </button>
        </div>
        {createIntegrator.isError && (
          <p className="text-destructive text-sm mb-2">{(createIntegrator.error as Error).message}</p>
        )}

        {/* list */}
        <div className="divide-y divide-border">
          {integratorsQuery.data?.length === 0 && (
            <p className="text-muted-foreground text-sm py-2">Интеграторов пока нет.</p>
          )}
          {integratorsQuery.data?.map((it) => (
            <div
              key={it.id}
              className={`flex items-center gap-3 py-2 cursor-pointer ${selectedIntegrator === it.id ? "bg-accent/40 rounded-md px-2 -mx-2" : ""}`}
              onClick={() => setSelectedIntegrator(it.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {it.displayName || it.userId}{" "}
                  <span className="text-xs text-muted-foreground">({it.kind})</span>
                </div>
                <div className="text-xs text-muted-foreground truncate">{it.userId}</div>
              </div>
              {it.certifiedAt ? (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <BadgeCheck className="w-4 h-4" /> сертифицирован
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">не сертифицирован</span>
              )}
              <button
                className={`${btn} border border-border`}
                onClick={(e) => {
                  e.stopPropagation();
                  certify.mutate({ id: it.id, certified: !it.certifiedAt });
                }}
              >
                {it.certifiedAt ? "Снять" : "Сертифицировать"}
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ ГРАНТЫ ============ */}
      <Section title="Гранты" icon={<KeyRound className="w-5 h-5 text-primary" />}>
        {!selectedIntegrator ? (
          <p className="text-muted-foreground text-sm">Выбери интегратора выше, чтобы управлять его грантами.</p>
        ) : (
          <>
            {/* create grant */}
            <div className="rounded-md border border-border p-3 mb-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <select className={input} value={grantScope} onChange={(e) => setGrantScope(e.target.value as "bot" | "account")}>
                  <option value="bot">конкретный бот</option>
                  <option value="account">весь аккаунт клиента</option>
                </select>
                {grantScope === "bot" ? (
                  <input className={input} placeholder="botId" value={grantBotId} onChange={(e) => setGrantBotId(e.target.value)} />
                ) : (
                  <input className={input} placeholder="targetUserId (клиент)" value={grantTargetUserId} onChange={(e) => setGrantTargetUserId(e.target.value)} />
                )}
                <label className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={grantAuto} onChange={(e) => setGrantAuto(e.target.checked)} />
                  autoApprove (live без ревью)
                </label>
              </div>
              <div className="flex flex-wrap gap-3">
                {CAPABILITIES.map((cap) => (
                  <label key={cap} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={grantCaps.includes(cap)} onChange={() => toggleCap(cap)} />
                    {cap}
                  </label>
                ))}
              </div>
              <button
                className={`${btn} bg-primary text-primary-foreground`}
                disabled={
                  grantCaps.length === 0 ||
                  (grantScope === "bot" ? !grantBotId.trim() : !grantTargetUserId.trim()) ||
                  createGrant.isPending
                }
                onClick={() => createGrant.mutate()}
              >
                Выдать грант
              </button>
              {createGrant.isError && (
                <p className="text-destructive text-sm">{(createGrant.error as Error).message}</p>
              )}
            </div>

            {/* grants list */}
            <div className="divide-y divide-border">
              {grantsQuery.data?.filter((g) => g.status === "active").length === 0 && (
                <p className="text-muted-foreground text-sm py-2">Активных грантов нет.</p>
              )}
              {grantsQuery.data
                ?.filter((g) => g.status === "active")
                .map((g) => (
                  <div key={g.id} className="flex items-center gap-3 py-2">
                    <div className="flex-1 min-w-0 text-sm">
                      <div className="truncate">
                        {g.scope === "bot" ? `бот ${g.botId}` : `аккаунт ${g.targetUserId}`}
                        {g.autoApprove && <span className="ml-2 text-xs text-green-600">live</span>}
                        {!g.autoApprove && <span className="ml-2 text-xs text-amber-600">needs-review</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{g.capabilities.join(", ")}</div>
                    </div>
                    <button
                      className={`${btn} border border-border text-destructive flex items-center gap-1`}
                      onClick={() => revokeGrant.mutate(g.id)}
                    >
                      <Trash2 className="w-4 h-4" /> Отозвать
                    </button>
                  </div>
                ))}
            </div>
          </>
        )}
      </Section>

      {/* ============ ОЧЕРЕДЬ РЕВЬЮ ============ */}
      <Section title="Очередь ревью (needs-review)" icon={<Clock className="w-5 h-5 text-primary" />}>
        <div className="divide-y divide-border">
          {pendingQuery.data?.length === 0 && (
            <p className="text-muted-foreground text-sm py-2">Нет правок, ожидающих ревью.</p>
          )}
          {pendingQuery.data?.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-2 text-sm">
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  <span className="font-medium">{p.changeType}</span> · бот {p.botId}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  интегратор {p.integratorId} · {p.createdAt}
                </div>
              </div>
              <button
                className={`${btn} bg-green-600 text-white flex items-center gap-1`}
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: p.id, decision: "approve" })}
              >
                <Check className="w-4 h-4" /> Применить
              </button>
              <button
                className={`${btn} border border-border text-destructive flex items-center gap-1`}
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: p.id, decision: "reject" })}
              >
                <X className="w-4 h-4" /> Отклонить
              </button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
